import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { LogIn, LogOut, Car, Search, RefreshCw, ShieldAlert, Clock } from "lucide-react";

const wrap = "ti-dark -m-3 min-h-[calc(100vh-3rem)] w-full max-w-full overflow-x-hidden px-4 py-5 pb-28 sm:-m-5 sm:px-6 lg:-m-5 lg:-mb-5 lg:px-8 lg:py-6 lg:pb-6";

const StatCard = ({ icon: Icon, label, value, tone, testid, onClick }) => (
  <button data-testid={testid} onClick={onClick} disabled={!onClick} className={`flex min-w-0 items-center gap-3 rounded-2xl border p-4 text-left transition-transform active:scale-[0.99] ${tone} ${onClick ? "hover:brightness-110" : "cursor-default"}`}>
    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10"><Icon size={24} /></span>
    <div className="min-w-0"><div className="text-[11px] uppercase tracking-wider opacity-80">{label}</div><div className="text-3xl font-extrabold">{value ?? "—"}</div></div>
  </button>
);

export default function SecurityDashboard() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/security/my-dashboard", { params: { q } }).then((r) => setD(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [q]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className={wrap} data-testid="security-dashboard-page">
      <div className="mx-auto w-full min-w-0 max-w-3xl">
        <div className="mb-5 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-500/12 text-blue-300 ring-1 ring-inset ring-blue-400/30"><ShieldAlert size={22} /></span>
            <div className="min-w-0"><h1 className="font-heading text-xl font-extrabold text-white">Dashboard Security</h1><p className="text-[11px] text-[#AAB8CC]">{d?.date || "…"} · Waktu server WIB</p></div>
          </div>
          <button data-testid="sec-dash-refresh" onClick={load} className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#29466D] bg-[#102544] text-[#C3D0E2]"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /></button>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <StatCard testid="sec-dash-in" icon={LogIn} label="Unit Masuk Hari Ini" value={d?.unit_in_today} tone="border-emerald-400/30 bg-emerald-500/10 text-emerald-200" onClick={() => nav("/security/masuk")} />
          <StatCard testid="sec-dash-out" icon={LogOut} label="Unit Keluar Hari Ini" value={d?.unit_out_today} tone="border-blue-400/30 bg-blue-500/10 text-blue-200" onClick={() => nav("/security/keluar")} />
          <StatCard testid="sec-dash-total" icon={Car} label="Total Unit di Bengkel" value={d?.total_in_workshop} tone="border-amber-400/30 bg-amber-500/10 text-amber-200" />
        </div>

        <div className="mt-4 flex gap-2">
          <button data-testid="sec-dash-btn-in" onClick={() => nav("/security/masuk")} className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/15 py-3 font-bold text-emerald-200"><LogIn size={18} /> UNIT MASUK</button>
          <button data-testid="sec-dash-btn-out" onClick={() => nav("/security/keluar")} className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-blue-400/30 bg-blue-500/15 py-3 font-bold text-blue-200"><LogOut size={18} /> UNIT KELUAR</button>
        </div>

        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-white">Unit Masih di Bengkel</h2>
            <span className="text-[11px] text-[#7E91AC]">{(d?.still_in_workshop || []).length} unit</span>
          </div>
          <div className="relative mb-3">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7E91AC]" />
            <input data-testid="sec-dash-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari Nopol / No WO…" className="h-11 w-full rounded-lg border border-[#29466D] bg-[#0a2038]/70 pl-9 pr-3 text-sm text-white placeholder:text-[#7E91AC] outline-none focus:border-[#1684FF]" />
          </div>
          <div className="space-y-2">
            {(d?.still_in_workshop || []).length === 0 ? (
              <p className="py-8 text-center text-sm text-[#7E91AC]">Tidak ada unit di bengkel.</p>
            ) : (d?.still_in_workshop || []).map((u) => (
              <div key={u.id} data-testid="sec-dash-unit-row" className="rounded-xl border border-white/8 bg-white/5 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-base font-extrabold tracking-wide text-white">{u.license_plate}</span>
                  <span className="flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-200"><Clock size={11} />{u.hours_in != null ? `${u.hours_in} jam` : "-"}</span>
                </div>
                <div className="mt-1 text-[12px] text-[#C3D0E2]">{u.vehicle}{u.wo_number && u.wo_number !== "-" ? ` · WO ${u.wo_number}` : ""}</div>
                <div className="mt-0.5 flex flex-wrap gap-x-2 text-[11px] text-[#AAB8CC]">
                  <span>Masuk {u.in_date} {u.in_time}</span><span>· Status: {u.stage}</span>
                </div>
                {u.note && u.note !== "-" && <div className="mt-1 text-[11px] text-[#8aa] [overflow-wrap:anywhere]">Ket: {u.note}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
